/**
 * お問い合わせフォーム (Joomla `contact.index` + `contact.corp`).
 * Dùng chung cho 2 trang — phân biệt bằng `data-contact-mode` = normal | corp.
 *
 * Flow 1:1 site cũ: form → 確認画面 (in-page) → POST → thankyou.
 *   - 種類 radio + text mẫu 詳細: GET /contact/kinds (port show_kindlist +
 *     formatTextChange). Chờ xong mới bỏ skeleton (rule §Loading state).
 *   - Validate hiển thị lỗi theo field từ 422 `errors` của Laravel (message
 *     tiếng Nhật đã port nguyên văn ở FormRequest).
 *   - Nút gửi hiện overlay 処理中 (rule §Processing overlay).
 *
 * 画像認証 KHÔNG port — honeypot `contact_url` (ẩn) + rate limit server.
 */
(function () {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  function boot() {
    const root = document.querySelector('[data-contact-page]');
    if (!root) return;

    const isCorp = root.dataset.contactMode === 'corp';
    const loggedIn = root.dataset.loggedIn === '1';

    const skeleton = root.querySelector('[data-contact-skeleton]');
    const form = root.querySelector('[data-contact-form]');
    const confirmPanel = root.querySelector('[data-contact-confirm]');
    const confirmList = root.querySelector('[data-contact-confirm-list]');
    const thankyou = root.querySelector('[data-contact-thankyou]');
    const kindsBox = root.querySelector('[data-contact-kinds]');
    const alertBox = root.querySelector('[data-contact-alert]');
    const confirmAlert = root.querySelector('[data-contact-confirm-alert]');
    if (!form || !kindsBox) return;

    // id → {kind, format_text} để dựng 確認画面 + auto-fill 詳細
    let kindMap = {};
    // payload đã gom ở bước form, gửi ở bước confirm
    let pending = null;

    // ===== 1. Load 種類 =====
    async function loadKinds() {
      let list = [];
      if (!window.inkApi) {
        if (skeleton) skeleton.remove();
        form.classList.remove('hidden');
        showAlert(alertBox, 'お問い合わせフォームを読み込めませんでした。お手数ですが時間をおいて再度お試しください。');
        return;
      }
      try {
        const res = await window.inkApi.get('/contact/kinds');
        const data = (res && res.data) || {};
        list = (isCorp ? data.corp : data.normal) || [];
      } catch (e) {
        console.warn('[contact] kinds fetch failed', e);
      }

      if (!list.length) {
        // Fail-open KHÔNG áp dụng được: không có 種類 thì không gửi được →
        // báo rõ thay vì để form trống im lặng.
        if (skeleton) skeleton.remove();
        showAlert(alertBox, 'お問い合わせフォームを読み込めませんでした。お手数ですが時間をおいて再度お試しください。');
        form.classList.remove('hidden');
        return;
      }

      kindsBox.innerHTML = list.map((k, i) => {
        kindMap[String(k.id)] = k;
        return '<label class="flex min-h-11 cursor-pointer items-center gap-2 rounded border border-ink-grayLine px-3 py-2">'
          + '<input type="radio" class="accent-ink-green" name="' + (isCorp ? 'kind' : 'subject') + '"'
          + ' value="' + esc(k.id) + '"' + (isCorp && i === 0 ? ' checked' : '') + '>'
          + '<span class="font-bold">' + esc(k.kind) + '</span>'
          + '</label>';
      }).join('');

      // Chọn 種類 → điền text mẫu vào 詳細 (formatTextChange). CHỈ ghi khi ô đang
      // trống hoặc đang giữ text mẫu cũ → không xoá nội dung khách đã gõ.
      const details = form.querySelector('[name="details"]');
      kindsBox.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!details) return;
          const tpl = (kindMap[radio.value] && kindMap[radio.value].format_text) || '';
          const current = details.value.trim();
          const isTemplate = Object.keys(kindMap).some((id) => {
            const t = (kindMap[id].format_text || '').trim();
            return t !== '' && current === t;
          });
          if (current === '' || isTemplate) details.value = tpl;
        });
      });

      if (skeleton) skeleton.remove();
      form.classList.remove('hidden');
    }

    // ===== 2. Helpers hiển thị lỗi =====
    function showAlert(el, msg) {
      if (!el) return;
      el.textContent = msg;
      el.classList.remove('hidden');
    }

    function clearErrors() {
      root.querySelectorAll('[data-error]').forEach((el) => {
        el.textContent = '';
        el.classList.add('hidden');
      });
      [alertBox, confirmAlert].forEach((el) => {
        if (el) { el.textContent = ''; el.classList.add('hidden'); }
      });
    }

    /** Map `errors` của Laravel → text dưới từng field; field lạ → alert chung. */
    function applyErrors(errors, fallbackAlert) {
      let unmatched = [];
      Object.keys(errors || {}).forEach((field) => {
        const msg = [].concat(errors[field]).join(' ');
        const target = root.querySelector('[data-error="' + field + '"]');
        if (target) {
          target.textContent = msg;
          target.classList.remove('hidden');
        } else {
          unmatched.push(msg);
        }
      });
      if (unmatched.length) showAlert(fallbackAlert, unmatched.join(' '));
      const firstErr = root.querySelector('[data-error]:not(.hidden)');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // ===== 3. Gom payload =====
    function gather() {
      const val = (name) => {
        const el = form.querySelector('[name="' + name + '"]');
        return el ? el.value.trim() : '';
      };
      const checked = form.querySelector('input[name="' + (isCorp ? 'kind' : 'subject') + '"]:checked');

      const payload = { contact_url: val('contact_url') };
      if (isCorp) {
        payload.kind = checked ? parseInt(checked.value, 10) : null;
        payload.corp = val('corp');
        payload.name = val('name');
        payload.details = val('details');
        if (!loggedIn) {
          payload.email = val('email');
          payload.phone = val('phone');
        }
      } else {
        payload.subject = checked ? parseInt(checked.value, 10) : null;
        payload.details = val('details');
        payload.order_number = val('order_number');
        if (!loggedIn) {
          payload.name = val('name');
          payload.email = val('email');
          payload.phone_1 = val('phone_1');
          payload.phone_2 = val('phone_2');
        }
      }
      return payload;
    }

    /** Rows 確認画面 — chỉ hiện field có giá trị (giống confirm tpl site cũ). */
    function confirmRows(p) {
      const kindLabel = (kindMap[String(isCorp ? p.kind : p.subject)] || {}).kind || '';
      const rows = isCorp
        ? [
            ['法人・事業主名', p.corp],
            ['ご担当者名', p.name],
            ['メールアドレス', p.email],
            ['電話番号', p.phone],
            ['お問い合わせ種類', kindLabel],
            ['詳細', p.details],
          ]
        : [
            ['お名前', p.name],
            ['メールアドレス', p.email],
            ['固定電話', p.phone_1],
            ['携帯電話', p.phone_2],
            ['お問い合わせ種類', kindLabel],
            ['注文番号', p.order_number],
            ['詳細', p.details],
          ];

      return rows
        .filter((r) => String(r[1] || '').trim() !== '')
        .map((r) => '<div class="grid gap-1 py-3 sm:grid-cols-[160px_minmax(0,1fr)] sm:gap-4">'
          + '<dt class="font-bold text-gray-700">' + esc(r[0]) + '</dt>'
          + '<dd class="whitespace-pre-wrap break-words">' + esc(r[1]) + '</dd>'
          + '</div>')
        .join('');
    }

    function showPanel(which) {
      form.classList.toggle('hidden', which !== 'form');
      if (confirmPanel) confirmPanel.classList.toggle('hidden', which !== 'confirm');
      if (thankyou) thankyou.classList.toggle('hidden', which !== 'thankyou');
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // ===== 4. form → confirm (validate ở server, mirror site cũ 2 bước) =====
    form.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      clearErrors();
      pending = gather();

      // Validate nhẹ client cho field bắt buộc để không phải chờ round-trip;
      // server vẫn là nơi quyết định (message giống nhau).
      const required = isCorp
        ? [['corp', '法人・事業主名を入力してください。'], ['name', 'ご担当者名を入力してください。']]
        : [['details', 'お問い合わせの詳細を入力してください。']];
      let localErrors = {};
      required.forEach(([field, msg]) => {
        if (!String(pending[field] || '').trim()) localErrors[field] = msg;
      });
      if (!(isCorp ? pending.kind : pending.subject)) {
        localErrors[isCorp ? 'kind' : 'subject'] = 'お問い合わせの種類を選択してください。';
      }
      if (Object.keys(localErrors).length) {
        applyErrors(localErrors, alertBox);
        return;
      }

      if (confirmList) confirmList.innerHTML = confirmRows(pending);
      showPanel('confirm');
    });

    // ===== 5. confirm → gửi =====
    const submitBtn = root.querySelector('[data-contact-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        if (!pending) return;
        clearErrors();
        if (!window.inkApi) {
          showAlert(confirmAlert, '送信に失敗しました。お手数ですが時間をおいて再度お試しください。');
          return;
        }
        submitBtn.disabled = true;
        window.inkProcessing?.show('送信中...');
        try {
          const res = await window.inkApi.post(isCorp ? '/contact/corp' : '/contact', pending);
          if (res && res.data && res.data.sent) {
            showPanel('thankyou');
            return;
          }
          if (res && res.errors) {
            // Lỗi field → về form để sửa
            showPanel('form');
            applyErrors(res.errors, alertBox);
            return;
          }
          showAlert(
            confirmAlert,
            (res && (res.message || (res.error && res.error.message)))
              || '送信に失敗しました。お手数ですが時間をおいて再度お試しください。',
          );
        } catch (e) {
          console.warn('[contact] submit failed', e);
          showAlert(confirmAlert, '送信に失敗しました。通信環境をご確認のうえ再度お試しください。');
        } finally {
          submitBtn.disabled = false;
          window.inkProcessing?.hide();
        }
      });
    }

    const backBtn = root.querySelector('[data-contact-back]');
    if (backBtn) backBtn.addEventListener('click', () => { clearErrors(); showPanel('form'); });

    loadKinds();
  }

  // api-client.js load cuối body → chờ event ink:api:ready (xem memory
  // project_inkapi_ready_theme_dev_verify), fallback load+1s.
  let booted = false;
  function startOnce() {
    if (booted) return;
    booted = true;
    boot();
  }
  if (window.inkApi) {
    startOnce();
  } else {
    document.addEventListener('ink:api:ready', startOnce, { once: true });
    window.addEventListener('load', () => {
      setTimeout(() => { if (!window.inkApi) startOnce(); }, 1000);
    });
  }
})();
